import classNames from 'classnames';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FAQCategory } from '../../interfaces/FAQ';
import FAQService from '../../services/FAQService';
import styles from './FAQ.module.scss';
import { FAQFilter } from './FAQFilter';
import { FAQHeader } from './FAQHeader';
import FAQNoSearchResults from './FAQNoSearchResults';
import { FAQSection } from './FAQSection';
interface FAQSectionsProps {
  faqSections: FAQCategory[];
}

export const FAQSections: FC<FAQSectionsProps> = ({ faqSections }) => {
  const categories = useMemo(() => faqSections.map((section) => section.category), [faqSections]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const { t } = useTranslation();

  const handleSearch = useCallback((search: string) => {
    if (selectedCategory){
      setSelectedCategory(null);
    }
    setSearchText(search);
  }, [selectedCategory]);

  const handleSelectCategory = useCallback((category: string) => {
    setSelectedCategory(prev => (prev === category ? null : category));

    if (searchText) {
      setSearchText('');
    }
  }, [searchText]);

  const filterAndGenerateFaqSectionProps = useCallback((section: FAQCategory) => {
    const { faqs, searchTexts } = FAQService.filterFAQs(section.articles, searchText);

    return {
      key: section.category,
      section: {
        ...section,
        articles: faqs
      },
      searchTexts,
    };
  }, [searchText]);

  const filteredSections = useMemo(() => {
    const categoryFiltered = selectedCategory
      ? faqSections.filter(section => section.category === selectedCategory)
      : faqSections;

    const searchFiltered = categoryFiltered
      .map(filterAndGenerateFaqSectionProps)
      .filter(({ section }) => section.articles.length > 0);

    return searchFiltered;
  }, [faqSections, filterAndGenerateFaqSectionProps, selectedCategory]);

  const hasSearchResults = filteredSections.length > 0;
  const searchResultCount = useMemo(() => {
    return searchText
      ? filteredSections.reduce((acc, { section }) => acc + section.articles.length, 0)
      : undefined;
  }, [searchText, filteredSections]);

  return (
    <div className={styles.faqSections}>
      <FAQHeader
        onSearch={handleSearch}
        searchResultCount={searchResultCount}
        searchText={searchText}
      />

      <section className={styles.faqSectionsList}>
        <div className={styles.whiteBgFiller} />
        {hasSearchResults && (
          <FAQFilter
            categories={categories}
            onSelectedCategory={handleSelectCategory}
            selectedCategory={selectedCategory}
          />
        )}
        {!!searchText && hasSearchResults && (
          <h2 className={classNames(styles.faqSectionTitle, styles.faqSectionTitleSearchResults)}>
            {t('faqSearchResults')} ‘<span className={styles.faqSectionTitleHighlight}>{searchText}</span>‘
          </h2>
        )}
        {hasSearchResults
          ? filteredSections.map(({ key, ...sectionProps }) => (<FAQSection key={key} {...sectionProps} />))
          : <FAQNoSearchResults onReset={() => handleSearch('')} />}
      </section>
    </div>
  );
};
